<template>
  <DxTabPanel
    :element-attr="tabPanelAttributes"
    :data-source="recalificareSteps"
    :loop="false"
    :selected-item="currentItem"
    :selected-index="currentStep"
    :animation-enabled="true"
    :swipe-enabled="false"
    :show-nav-buttons="false"
    @selection-changed="tabSelectionChanged($event)"
  >
    <template #item="{ data: step }">
      <div>
        <DxScrollView ref="recalificareInputScroll" v-show="step == 1" class="recalificare-input-scroll" :use-native="true">
          <div class="recalificare_input_holder" :class="[{ input_holder_error: dateError }]">
            <div @click="showCalendarSelector">
              <div class="input_label">{{ $t("common.date") }}</div>
              <div class="input_value">
                {{ recalificareData.recalificare_date }}
              </div>
            </div>
            <div v-if="is_date_selector_visible">
              <FunctionalCalendar
                :date-format="getDefaultDateFormat.toLowerCase()"
                :is-date-range="false"
                v-model="calendarData"
                :change-month-function="true"
                :change-year-function="true"
                :is-date-picker="true"
                @choseDay="calendarDayChosen"
                @input="onDateSelected"
              ></FunctionalCalendar>
            </div>
          </div>
          <div class="recalificare_input_holder" @click="showSourceSelector" :class="[{ input_holder_error: sourceError }]">
            <div class="recalificare_parcel_select_edit">
              <div class="recalificare_parcel_info">
                <div class="input_label">{{ $t("stock_mobile.form.source_product") }}</div>
                <div v-if="sourceProduct" class="input_value">{{ sourceProduct.name }}</div>
                <div v-else class="input_value_placeholder" style="color: #989696 !important">{{ $t("stock_mobile.form.select_source_product_from_stock") }}</div>
                <div class="recalificare_input_second_value">
                  {{ sourceSecondaryText }}
                </div>
              </div>
              <div class="recalificare_parcel_edit_icon">
                <i class="fa-regular fa-edit"></i>
              </div>
            </div>
          </div>
          <div class="recalificare_input_holder" @click="showDestSelector" :class="[{ input_holder_error: destError }]">
            <div class="recalificare_parcel_select_edit">
              <div class="recalificare_parcel_info">
                <div class="input_label">{{ $t("stock_mobile.form.destination_product") }}</div>
                <div v-if="destProduct" class="input_value">{{ destProduct.name }}</div>
                <div v-else class="input_value_placeholder" style="color: #989696 !important">{{ $t("stock_mobile.form.select_destination_product") }}</div>
                <div class="recalificare_input_second_value">
                  {{ destSecondaryText }}
                </div>
              </div>
              <div class="recalificare_parcel_edit_icon">
                <i class="fa-regular fa-edit"></i>
              </div>
            </div>
          </div>
          <div class="recalificare_input_holder">
            <div class="input_label">{{ $t("common.notes") }}</div>
            <div class="input_value">
              <DxTextArea v-model="recalificareData.notes" class="global_dx_number_input" :height="45" />
            </div>
          </div>
          <div class="input-bottom-spacer"></div>
        </DxScrollView>
        <div class="recalificare_bottom_sticky_two_line" v-show="step == 1 && canSave && !saveButtonPressed">
          <div class="sticky_action_button" @click="saveRecalificare(true)">{{ $t("stock_mobile.form.save_and_new_reclassification") }}</div>
          <div class="sticky_action_button_second" @click="saveRecalificare(false)">{{ $t("common.save") }}</div>
        </div>
        <ProductSelector
          ref="sourceProductSelector"
          :showPopup="sourceSelectorVisibility"
          @hidden="hideSourceSelector"
          :selectedProducts="recalificareData.source"
          :selectFromStock="true"
          :allowAddNew="false"
          :multipleSelection="false"
          :useUnitPrice="false"
          :movementDate="recalificareData.recalificare_date"
          :transactionType="transactionTypeTitle"
        >
        </ProductSelector>
        <ProductSelector
          ref="destProductSelector"
          :showPopup="destSelectorVisibility"
          @hidden="hideDestSelector"
          :selectedProducts="recalificareData.dest"
          :selectFromStock="false"
          :allowAddNew="true"
          :multipleSelection="false"
          :useUnitPrice="false"
          :transactionType="transactionTypeTitle"
        >
        </ProductSelector>
        <toast-message ref="toastMessageRecalificare"></toast-message>
      </div>
    </template>
  </DxTabPanel>
</template>

<script>
import { mapActions, mapGetters } from "vuex";
import DxTabPanel from "devextreme-vue/tab-panel";
import { DxScrollView } from "devextreme-vue/scroll-view";
import DxTextArea from "devextreme-vue/text-area";
import ToastMessage from "@/components/Mobile/toastMessage.vue";
import { FunctionalCalendar } from "vue-functional-calendar";
import moment from "moment";
import ProductSelector from "../Product/ProductSelector.vue";
import { TransactionActions, TransactionOptions } from "../transactions.js";

export default {
  name: "Recalificare",
  data() {
    return {
      preselectDate: true,
      calendarData: {},
      tabPanelAttributes: {
        class: "recalificare_tabs",
      },
      recalificareSteps: [1],
      currentStep: 0,
      currentItem: 1,
      recalificareData: {
        recalificare_date: null,
        notes: "",
        source: [],
        dest: [],
      },
      is_date_selector_visible: false,
      sourceSelectorVisibility: false,
      destSelectorVisibility: false,
      saveButtonPressed: false,
      validationTouched: false,
    };
  },
  components: {
    DxTabPanel,
    DxScrollView,
    DxTextArea,
    ToastMessage,
    FunctionalCalendar,
    ProductSelector,
  },
  computed: {
    ...mapGetters({
      getDefaultDateFormat: "settings/getDefaultDateFormat",
    }),
    transactionTypeTitle() {
      return TransactionOptions.find((opt) => opt.code === TransactionActions.Recalificare)?.title || "";
    },
    sourceProduct() {
      return this.recalificareData.source.length > 0 ? this.recalificareData.source[0] : null;
    },
    destProduct() {
      return this.recalificareData.dest.length > 0 ? this.recalificareData.dest[0] : null;
    },
    sourceSecondaryText() {
      if (!this.sourceProduct) {
        return "";
      }
      const symbol = this.sourceProduct?.measure_unit?.symbol ?? "";
      const deptSuffix = this.sourceProduct?.department_name ? ` (${this.sourceProduct.department_name})` : "";
      const warehouse = this.sourceProduct?.warehouse_name ? this.sourceProduct.warehouse_name + deptSuffix + " · " : "";
      return `${warehouse}${this.sourceProduct.quantity ?? 0} ${symbol}`.trim();
    },
    destSecondaryText() {
      if (!this.destProduct) {
        return "";
      }
      const symbol = this.destProduct?.measure_unit?.symbol ?? "";
      return `${this.destProduct.quantity ?? 0} ${symbol}`.trim();
    },
    dateError() {
      return this.validationTouched && !this.recalificareData.recalificare_date;
    },
    sourceError() {
      return this.validationTouched && !this.isSourceValid;
    },
    destError() {
      return this.validationTouched && !this.isDestValid;
    },
    isSourceValid() {
      const product = this.sourceProduct;
      if (!product || !product.uuid || !product.warehouse_uuid) {
        return false;
      }
      const qty = parseFloat(product.quantity) || 0;
      const max = parseFloat(product.warehouse_quantity);
      if (qty <= 0) {
        return false;
      }
      if (Number.isFinite(max) && qty > max) {
        return false;
      }
      if (product.lot_management) {
        const items = Array.isArray(product.lot_items) ? product.lot_items : [];
        if (items.length === 0) {
          return false;
        }
        const sum = items.reduce((acc, item) => acc + (parseFloat(item.quantity) || 0), 0);
        if (sum <= 0 || Math.abs(sum - qty) > 0.0001) {
          return false;
        }
        return items.every((item) => (parseFloat(item.quantity) || 0) > 0);
      }
      return true;
    },
    isDestValid() {
      const product = this.destProduct;
      if (!product || !product.uuid) {
        return false;
      }
      return (parseFloat(product.quantity) || 0) > 0;
    },
    canSave() {
      return !!this.recalificareData.recalificare_date && this.isSourceValid && this.isDestValid;
    },
  },
  methods: {
    ...mapActions({
      createRequalification: "smTransactions/createRequalificationTransaction",
      getGroupByCode: "settings/getGroupByCode",
    }),
    tabSelectionChanged(e) {
      this.currentItem = e.addedItems[0];
      this.currentStep = this.recalificareSteps.indexOf(this.currentItem);
    },
    getCurrentStep() {
      return this.currentStep;
    },
    setTitle() {
      this.$emit("setTitle", this.$t("stock_mobile.form.product_reclassification"), true);
    },
    goBack() {
      this.$emit("goBackParent");
    },
    closeTransactions(message) {
      this.$emit("closeTransactions", message);
    },
    showCalendarSelector() {
      this.is_date_selector_visible = !this.is_date_selector_visible;
    },
    calendarDayChosen() {
      this.is_date_selector_visible = false;
    },
    onDateSelected(date) {
      this.recalificareData.recalificare_date = date.selectedDate;
    },
    showSourceSelector() {
      if (!this.recalificareData.recalificare_date) {
        this.$refs.toastMessageRecalificare.showError(this.$t("stock_mobile.form.reclassification_date_required"));
        return;
      }
      this.sourceSelectorVisibility = true;
    },
    hideSourceSelector() {
      this.sourceSelectorVisibility = false;
      this.recalificareData.source = this.recalificareData.source.slice();
    },
    showDestSelector() {
      this.destSelectorVisibility = true;
    },
    hideDestSelector() {
      this.destSelectorVisibility = false;
      this.recalificareData.dest = this.recalificareData.dest.slice();
    },
    saveRecalificare(saveAndNew) {
      this.validationTouched = true;
      if (!this.canSave) {
        this.$refs.toastMessageRecalificare.showError(this.createErrorMessages());
        return;
      }

      this.saveButtonPressed = true;

      const source = this.sourceProduct;
      const dest = this.destProduct;

      const sourceForSave = {
        uuid: source.uuid,
        quantity: source.quantity,
        lot_items: Array.isArray(source.lot_items) ? source.lot_items : [],
      };

      const destForSave = {
        name: dest.name,
        measure_unit_id: dest.measure_unit_id ?? dest.measure_unit?.id ?? null,
        quantity: dest.quantity,
      };
      if (!dest.uuid.toString().startsWith("NEW_")) {
        destForSave.uuid = dest.uuid;
      } else if (dest.product_type_uuid) {
        destForSave.product_type_uuid = dest.product_type_uuid;
      }

      const recalificareForSave = {
        requalification_date: this.recalificareData.recalificare_date,
        warehouse_uuid: source.warehouse_uuid,
        notes: this.recalificareData.notes,
        source_product: sourceForSave,
        dest_product: destForSave,
      };

      this.createRequalification(recalificareForSave)
        .then(() => {
          this.$refs.toastMessageRecalificare.showSuccess(this.$t("stock_mobile.form.reclassification_saved"));
          if (saveAndNew) {
            this.resetForm();
            this.saveButtonPressed = false;
          } else {
            this.closeTransactions(this.$t("stock_mobile.form.reclassification_saved"));
          }
        })
        .catch((e) => {
          this.saveButtonPressed = false;
          const status = e?.response?.status;
          const backendMessage = e?.response?.data?.errors?.error || e?.response?.data?.message;

          if ((status === 400 || status === 500) && backendMessage) {
            this.$refs.toastMessageRecalificare.showError(backendMessage);
          } else {
            this.$refs.toastMessageRecalificare.showError(this.$t("stock_mobile.form.error_reclassification_save_failed"));
          }
        });
    },
    createErrorMessages() {
      const errorMessages = [];

      if (!this.recalificareData.recalificare_date) {
        errorMessages.push(this.$t("stock_mobile.form.error_reclassification_date_required"));
      }

      const source = this.sourceProduct;
      if (!source) {
        errorMessages.push(this.$t("stock_mobile.form.error_source_product_required"));
      } else {
        if (!source.warehouse_uuid) {
          errorMessages.push(this.$t("stock_mobile.form.error_source_warehouse_required", { name: source.name }));
        }
        const qty = parseFloat(source.quantity) || 0;
        const max = parseFloat(source.warehouse_quantity);
        if (qty <= 0) {
          errorMessages.push(this.$t("stock_mobile.form.error_quantity_gt_zero_for", { name: source.name }));
        } else if (Number.isFinite(max) && qty > max) {
          errorMessages.push(this.$t("stock_mobile.form.error_quantity_exceeds_stock_for", { name: source.name }));
        }
        if (source.lot_management) {
          const items = Array.isArray(source.lot_items) ? source.lot_items : [];
          const sum = items.reduce((acc, item) => acc + (parseFloat(item.quantity) || 0), 0);
          if (Math.abs(sum - qty) > 0.0001) {
            errorMessages.push(this.$t("stock_mobile.form.error_lot_sum_must_match"));
          }
        }
      }

      const dest = this.destProduct;
      if (!dest) {
        errorMessages.push(this.$t("stock_mobile.form.error_destination_product_required"));
      } else if ((parseFloat(dest.quantity) || 0) <= 0) {
        errorMessages.push(this.$t("stock_mobile.form.error_quantity_gt_zero_for", { name: dest.name }));
      }

      return errorMessages;
    },
    getSettings() {
      this.getGroupByCode({
        code: "STOCK_MANAGEMENT",
      }).then((response) => {
        const preselectSetting = response.items.find((obj) => obj.code == "USE_CURRENT_DATE_ON_CREATION");
        this.preselectDate = preselectSetting?.value == "1";
        if (this.preselectDate) {
          this.recalificareData.recalificare_date = moment().format(this.getDefaultDateFormat);
        }
      });
    },
    resetForm() {
      this.validationTouched = false;
      this.recalificareData = {
        recalificare_date: this.preselectDate ? moment().format(this.getDefaultDateFormat) : null,
        notes: "",
        source: [],
        dest: [],
      };
    },
  },
  mounted() {
    this.getSettings();
    this.setTitle();
  },
};
</script>

<style lang="scss">
@import "@/assets/scss/stockMobile/recalificare.scss";
</style>
